import { packTile } from "../document/packed-tile.ts";
import type { CellRect } from "./match.ts";
import { extendingColumn, isTileId } from "./face-columns.ts";
import type { CompiledFace } from "./faces.ts";
import type { TerrainFace, TerrainFaceSide } from "./types.ts";

/**
 * Side columns beside a face (ADR-072's amendment of 2026-09-26). Where a plateau's south edge steps, the
 * rules draw the lower part's side next to the upper face over the face's own rows. When the face column
 * beside it extends, the side is drawn as tall: its repeating row runs down over the plain side tiles it may
 * draw over, ending in its own foot on the face's foot row, or, where it meets the lower face's lip, in the
 * join tile there and a plain fill down to the face's foot. The side stage runs after every face column and
 * its cells replace a face's; each decision reads only the rules' output.
 */

/**
 * A side declaration packed against a tileset: its columns' packed cells in one array of the face's rows per
 * column, and the packed tiles its walk accepts and writes.
 */
export interface CompiledSide {
  readonly beside: number;
  readonly repeatRow: number;
  readonly columns: Uint32Array;
  readonly overwrites: Uint32Array;
  readonly joinLips: Uint32Array;
  readonly join: number;
  readonly fill: number;
}

/**
 * One side column a packed lip starts: the side's index and the column's index within it.
 */
export interface SideColumnRef {
  readonly side: number;
  readonly column: number;
}

/**
 * The first problem with a face's side declarations, or null when they are well formed: `beside` -1 or 1, at
 * least one column, every column the face's rows long, `repeatRow` strictly inside, every id a local tile id,
 * no side column equal to a face column, no side's lip among any side's `overwrites` or `joinLips`, `join`
 * among `joinLips`, and every one of `joinLips` the lip of a face column, so the lower face a side meets is
 * one the pass extends.
 *
 * @param face The declaration, its groups already well formed.
 * @param rows The face's rows.
 * @param faceColumns Every face column's ids joined by `/`.
 * @returns The reason, or null.
 */
export function sidesProblem(face: TerrainFace, rows: number, faceColumns: ReadonlySet<string>): string | null {
  const sides = face.sides ?? [];
  const faceLips = new Set(face.groups.flat().map((column) => column[0] as number));
  const sideLips = new Set(sides.flatMap((side) => side.columns.map((column) => column[0] as number)));
  for (const [s, side] of sides.entries()) {
    const problem = sideProblem(side, rows, faceColumns, faceLips, sideLips);
    if (problem !== null) {
      return `side ${s} ${problem}`;
    }
  }
  return null;
}

/**
 * The first problem with one side declaration, or null.
 *
 * @param side The side.
 * @param rows The face's rows.
 * @param faceColumns Every face column's ids joined by `/`.
 * @param faceLips Every face column's lip.
 * @param sideLips Every side column's lip.
 * @returns The reason without the side's index, or null.
 */
function sideProblem(side: TerrainFaceSide, rows: number, faceColumns: ReadonlySet<string>, faceLips: ReadonlySet<number>, sideLips: ReadonlySet<number>): string | null {
  if (side.beside !== -1 && side.beside !== 1) {
    return `has beside ${String(side.beside)}, which is neither -1 nor 1`;
  }
  if (side.columns.length === 0) {
    return "has no column";
  }
  const short = side.columns.find((column) => column.length !== rows);
  if (short !== undefined) {
    return `has a column of ${short.length} rows where the face's columns have ${rows}`;
  }
  if (!Number.isInteger(side.repeatRow) || side.repeatRow <= 0 || side.repeatRow >= rows - 1) {
    return `has repeatRow ${side.repeatRow}, not a row strictly between the first and the last of ${rows}`;
  }
  const bad = [...side.columns.flat(), ...side.overwrites, ...side.joinLips, side.join, side.fill].find((tile) => !isTileId(tile));
  if (bad !== undefined) {
    return `names ${bad}, which is not a tile id`;
  }
  const face = side.columns.find((column) => faceColumns.has(column.join("/")));
  if (face !== undefined) {
    return `declares ${face.join("/")}, which is a face column`;
  }
  const lip = [...side.overwrites, ...side.joinLips].find((tile) => sideLips.has(tile));
  if (lip !== undefined) {
    return `may draw over ${lip}, a side's lip`;
  }
  if (!side.joinLips.includes(side.join)) {
    return `draws join ${side.join}, which is not among its joinLips`;
  }
  const loose = side.joinLips.find((tile) => !faceLips.has(tile));
  if (loose !== undefined) {
    return `meets ${loose}, which is not the lip of a face column`;
  }
  return null;
}

/**
 * Every local tile id a face declaration names, sides included, for checking them against a tileset.
 *
 * @param face The declaration.
 * @returns The ids, in declaration order, repeats kept.
 */
export function faceTiles(face: TerrainFace): number[] {
  const sides = (face.sides ?? []).flatMap((side) => [...side.columns.flat(), ...side.overwrites, ...side.joinLips, side.join, side.fill]);
  return [...face.groups.flat(2), ...sides];
}

/**
 * Packs a face's side declarations against a tileset.
 *
 * @param face The declaration, well formed.
 * @param rows The face's rows.
 * @param tilesetIndex The terrain tileset's index in the world's tilesets.
 * @returns The compiled sides and the side columns each packed lip starts.
 * @throws RangeError when the tileset index cannot be packed.
 */
export function compileSides(face: TerrainFace, rows: number, tilesetIndex: number): { sides: CompiledSide[]; sideLips: Map<number, SideColumnRef[]> } {
  const sideLips = new Map<number, SideColumnRef[]>();
  const pack = (tiles: readonly number[]): Uint32Array => Uint32Array.from(tiles, (tile) => packTile(tilesetIndex, tile));
  const sides = (face.sides ?? []).map((side, s): CompiledSide => {
    const columns = pack(side.columns.flat());
    side.columns.forEach((_, c) => {
      const lip = columns[c * rows] as number;
      sideLips.set(lip, [...(sideLips.get(lip) ?? []), { side: s, column: c }]);
    });
    return { beside: side.beside, repeatRow: side.repeatRow, columns, overwrites: pack(side.overwrites), joinLips: pack(side.joinLips), join: packTile(tilesetIndex, side.join), fill: packTile(tilesetIndex, side.fill) };
  });
  return { sides, sideLips };
}

/**
 * Whether a packed tile is among a list.
 *
 * @param tiles The list.
 * @param tile The packed tile.
 * @returns True when listed.
 */
function listed(tiles: Uint32Array, tile: number): boolean {
  for (let i = 0; i < tiles.length; i += 1) {
    if (tiles[i] === tile) {
      return true;
    }
  }
  return false;
}

/**
 * Whether the rules' output holds a side column from its lip down to its repeating row at a position.
 *
 * @param side The side.
 * @param column The column's index within the side.
 * @param rows The face's rows.
 * @param source The rules' output.
 * @param width Map width.
 * @param x Column.
 * @param y The lip's row.
 * @returns True on a match.
 */
function matchesSide(side: CompiledSide, column: number, rows: number, source: Readonly<Uint32Array>, width: number, x: number, y: number): boolean {
  for (let r = 1; r <= side.repeatRow; r += 1) {
    if (source[(y + r) * width + x] !== side.columns[column * rows + r]) {
      return false;
    }
  }
  return true;
}

/**
 * The row a side's walk meets a lower face's lip, the face's foot row plus one when it meets none, or -1 when
 * the walk meets a tile it may not draw over. The walk runs from the row below the side's repeating row to
 * the face's foot row and accepts the side's own rows below its repeating row and its `overwrites`.
 *
 * @param side The side.
 * @param column The column's index within the side.
 * @param rows The face's rows.
 * @param source The rules' output.
 * @param width Map width.
 * @param x Column.
 * @param y The lip's row.
 * @param foot The face's foot row, inside the map.
 * @returns The join's row, `foot + 1`, or -1.
 */
function walk(side: CompiledSide, column: number, rows: number, source: Readonly<Uint32Array>, width: number, x: number, y: number, foot: number): number {
  for (let yy = y + side.repeatRow + 1; yy <= foot; yy += 1) {
    const tile = source[yy * width + x] as number;
    if (listed(side.joinLips, tile)) {
      return yy;
    }
    if (listed(side.overwrites, tile)) {
      continue;
    }
    let own = false;
    for (let r = side.repeatRow + 1; r < rows && !own; r += 1) {
      own = side.columns[column * rows + r] === tile;
    }
    if (!own) {
      return -1;
    }
  }
  return foot + 1;
}

/**
 * Draws every side column whose face beside it extends, over the faces already drawn in `output`. A side at
 * `(x, y)` extends when the face column at `(x + beside, y)` extends, its walk down to the face's foot row
 * meets only tiles it may draw over, and, when the walk meets a lower face's lip at a row, that face column
 * extends too. It then draws its repeating row below it, and either its own rows below the repeating row
 * ending on the face's foot row, or, from the lip it met, `join` and `fill` down to the face's foot row.
 *
 * @param face The compiled face.
 * @param source The rules' output.
 * @param output The buffer holding the faces' extensions.
 * @param width Map width.
 * @param height Map height.
 * @param faceHeight Rows the face takes, above its own rows.
 * @param scan Lip positions to scan, inside the map.
 * @param clip Cells that may be written, inside the map.
 */
export function drawSides(face: CompiledFace, source: Readonly<Uint32Array>, output: Uint32Array, width: number, height: number, faceHeight: number, scan: CellRect, clip: CellRect): void {
  const { rows } = face;
  const extra = faceHeight - rows;
  const yEnd = Math.min(scan.y1, height - faceHeight + 1);
  const x0 = Math.max(scan.x0, clip.x0);
  const x1 = Math.min(scan.x1, clip.x1);
  for (let y = scan.y0; y < yEnd; y += 1) {
    for (let x = x0; x < x1; x += 1) {
      const refs = face.sideLips.get(source[y * width + x] as number);
      if (refs === undefined) {
        continue;
      }
      for (const ref of refs) {
        const side = face.sides[ref.side] as CompiledSide;
        if (!matchesSide(side, ref.column, rows, source, width, x, y)) {
          continue;
        }
        const foot = y + faceHeight - 1;
        const join = walk(side, ref.column, rows, source, width, x, y, foot);
        if (join >= 0 && extendingColumn(face, source, width, height, x + side.beside, y, faceHeight) >= 0 && (join > foot || extendingColumn(face, source, width, height, x, join, faceHeight) >= 0)) {
          const repeat = side.columns[ref.column * rows + side.repeatRow] as number;
          const repeatEnd = join > foot ? y + side.repeatRow + extra : join - 1;
          const top = Math.max(clip.y0, y + side.repeatRow + 1);
          const bottom = Math.min(clip.y1, foot + 1);
          for (let yy = top; yy < bottom; yy += 1) {
            let tile: number;
            if (yy <= repeatEnd) {
              tile = repeat;
            } else if (join > foot) {
              tile = side.columns[ref.column * rows + yy - y - extra] as number;
            } else {
              tile = yy === join ? side.join : side.fill;
            }
            output[yy * width + x] = tile;
          }
        }
        break;
      }
    }
  }
}
